import { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Chip,
    Typography,
} from "@mui/material";
import {
    useGetNotificationConfig,
    useUpdateNotificationConfig,
} from "../../hooks/price-change-schedules";
import { useMessage } from "../../sdk/hooks";

interface Props {
    open: boolean;
    onClose: () => void;
}

export function NotificationSettingsDialog({ open, onClose }: Props) {
    const { showError, showSuccess } = useMessage();
    const { data: config } = useGetNotificationConfig("notificationConfig");
    const updateConfig = useUpdateNotificationConfig("notificationConfig");

    const [emails, setEmails] = useState<string[]>([]);
    const [inputValue, setInputValue] = useState("");

    useEffect(() => {
        if (config?.emails) {
            setEmails(config.emails);
        }
    }, [config]);

    const handleAdd = () => {
        const email = inputValue.trim();
        if (!email) return;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            showError("Invalid email address");
            return;
        }

        if (emails.includes(email)) {
            showError("Email already added");
            return;
        }

        setEmails([...emails, email]);
        setInputValue("");
    };

    const handleRemove = (emailToRemove: string) => {
        setEmails(emails.filter((e) => e !== emailToRemove));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAdd();
        }
    };

    const handleSave = async () => {
        try {
            await updateConfig.mutateAsync(emails);
            showSuccess("Notification settings saved");
            onClose();
        } catch (err: any) {
            showError(err.response?.data?.message || err.message);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Notification Settings</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="textSecondary" mb={2}>
                    These email addresses will be notified when scheduled price
                    changes are activated or reverted.
                </Typography>

                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1, mb: 2 }}>
                    <TextField
                        label="Email address"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        size="small"
                        fullWidth
                    />
                    <Button
                        variant="outlined"
                        onClick={handleAdd}
                        sx={{ minWidth: { xs: "100%", sm: "auto" } }}
                    >
                        Add
                    </Button>
                </Box>

                <Box display="flex" flexWrap="wrap" gap={1}>
                    {emails.map((email) => (
                        <Chip
                            key={email}
                            label={email}
                            onDelete={() => handleRemove(email)}
                        />
                    ))}
                    {emails.length === 0 && (
                        <Typography variant="body2" color="textSecondary">
                            No notification emails configured
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={updateConfig.isLoading}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
